import React from 'react';

import { Box, IconButton, Typography } from '@mui/material';
import { ErrorDotIndicator } from '@sharedCR/common/ErrorDotIndicator';
import NavNeededIndicator from '@sharedCR/common/NavNeededIndicator';
import { navNeededRowStyles } from '@sharedCR/common/NavNeededIndicator/NavNeededIndicator.styles';
import {
  getNavNeededState,
  getTabAreaIdsBySectionHref,
} from '@sharedCR/common/NavNeededIndicator/NavNeededIndicator.utils';
import PendingChangeWrapper from '@sharedCR/common/PendingChangeWrapper';
import { PresenceSectionDot } from '@sharedCR/common/PresenceDot';
import { LoansPaths } from 'app/configs/routesConfig/loans';
import { checkIsTabHidden } from 'app/pages/system/autoLockRules/AutoLockRules.utils';
import { DeleteIcon, ResetIcon } from 'app/shared/componentsRestyled/coreDesignSystem/icons';
import _ from 'lodash';

import { ConfirmationModalType } from 'constants/confirmationDialog';
import { ValueStub } from 'constants/data';
import { PendingChangesActionType } from 'constants/pendingChanges';
import { calculatePendingChanges } from 'utils/pendingChanges';

import { openConfirmationDialogAction } from 'store/global/actions';
import {
  bulkUpdateLoanContactsAction,
  editLoanAndLockSummaryAction,
  editPropertyAndLoanSummaryAction,
  getChattelDetailsAction,
  getLoanAndLockSummaryEditDataAction,
  getLoanContactsAction,
  getPropertyAndLoanSummaryEditDataAction,
  saveChattelDetailsAction,
} from 'store/loanApplications/actions';

import { PropertyAndLoanTestIds } from '../PropertyAndLoan.testIds';

import AdditionalFinancing from './Content/MainContent/AdditionalFinancing';
import AgentAndSeller from './Content/MainContent/AgentAndSeller';
import { agentsAndSellersPendingChangesSectionKey } from './Content/MainContent/AgentAndSeller/AgentAndSeller.constants';
import ApplicationCustomFields from './Content/MainContent/ApplicationCustomFields';
import Appraisal from './Content/MainContent/Appraisal';
import { APPRAISAL_ORDER_CONTACTS_KEY } from './Content/MainContent/Appraisal/components/AppraisalOrderContactsCard/AppraisalOrderContactsCard.constants';
import ChattelHomeDetails from './Content/MainContent/ChattelHomeDetails';
import { chattelHomeDetailsSectionFieldsKeys } from './Content/MainContent/ChattelHomeDetails/ChattelHomeDetails.constants';
import ChattelProperty from './Content/MainContent/ChattelProperty';
import { chattelPropertySectionFieldsKeys } from './Content/MainContent/ChattelProperty/ChattelProperty.constants';
import ChattelPropertyExpenses from './Content/MainContent/ChattelPropertyExpenses';
import { chattelPropertyExpensesSectionFieldsKeys } from './Content/MainContent/ChattelPropertyExpenses/ChattelPropertyExpenses.constants';
import Contacts from './Content/MainContent/Contacts';
import { contactsFieldsKeys } from './Content/MainContent/Contacts/Contacts.config';
import { ContactsField } from './Content/MainContent/Contacts/Contacts.constants';
import Credits from './Content/MainContent/Credits';
import LoanInfo from './Content/MainContent/LoanInfo';
import { loanInfoSectionFieldsKeys } from './Content/MainContent/LoanInfo/LoanInfo.config';
import LoanPL from './Content/MainContent/LoanPL';
import LoanRoles from './Content/MainContent/LoanRoles';
import LoanTags from './Content/MainContent/LoanTags';
import LockInfo from './Content/MainContent/LockInfo/LockInfo';
import { lockInfoSectionFieldsKeys } from './Content/MainContent/LockInfo/LockInfo.config';
import MortgageInsurance from './Content/MainContent/MortgageInsurance';
import { mortgageInsuranceSectionFieldsKeys } from './Content/MainContent/MortgageInsurance/MortgageInsurance.config';
import OBCustomFields from './Content/MainContent/OBCustomFields';
import ProgramsAndPricing from './Content/MainContent/ProgramsAndPricing';
import { programAndPricingPendingChangesFields } from './Content/MainContent/ProgramsAndPricing/ProgramAndPricing.constants';
import Project from './Content/MainContent/Project';
import Property from './Content/MainContent/Property';
import { propertySectionFieldsKeys } from './Content/MainContent/Property/Property.config';
import PropertyCustomFields from './Content/MainContent/PropertyCustomFields';
import PropertyExpenses from './Content/MainContent/PropertyExpenses';
import { propertyExpensesPendingChangesFieldsKeys } from './Content/MainContent/PropertyExpenses/PropertyExpenses.constants';
import SellSideLock from './Content/MainContent/SellSideLock';
import { sellSideLockSectionFieldsKeys } from './Content/MainContent/SellSideLock/SellSideLock.config';
import TransactionDetails from './Content/MainContent/TransactionDetails';
import { transactionDetailsFieldsKeys } from './Content/MainContent/TransactionDetails/TransactionDetails.config';
import {
  ENTITY_TYPE_HREF_MAP,
  LOAN_AND_LOCK_SECTION_HREF_MAP,
  PROPERTY_SECTION_HREF_MAP,
} from './EditModal.hrefs';
import {
  navTabButtonStyles,
  navTabCustomStyles,
  navTextContentStyles,
  navTextStyles,
  navTextWrapperStyles,
  pendingChangesCounterStyles,
} from './EditModal.styles';

export {
  ENTITY_TYPE_HREF_MAP,
  LOAN_AND_LOCK_SECTION_HREF_MAP,
  PROPERTY_AND_LOAN_ENTITY_TYPES,
  PROPERTY_SECTION_HREF_MAP,
} from './EditModal.hrefs';

export const ENTITIES_WITH_DYNAMIC_SECTIONS = [ENTITY_TYPE_HREF_MAP.CONTACTS];

export const ENTITY_MODAL_TITLE_MAP = {
  [ENTITY_TYPE_HREF_MAP.PROPERTY_INFO]: 'Property',
  [ENTITY_TYPE_HREF_MAP.LOAN_AND_LOCK]: 'Loan & Lock Info',
  [ENTITY_TYPE_HREF_MAP.CONTACTS]: 'Contacts',
  [ENTITY_TYPE_HREF_MAP.CHATTEL]: 'Chattel Details',
};

const CHATTEL_SECTION_HREF_MAP = {
  PROPERTY: 'property',
  PROPERTY_EXPENSES: 'property-expenses',
  HOME_DETAILS: 'home-details',
};

export const ADD_MORTGAGE_HREF = 'add-mortgage';

export const getCustomFieldsSectionOverrides = ({ subtab, section }) => ({
  isPropertyCustomFieldsActive:
    subtab === ENTITY_TYPE_HREF_MAP.PROPERTY_INFO &&
    section === PROPERTY_SECTION_HREF_MAP.CUSTOM_FIELDS,
  isApplicationCustomFieldsActive:
    subtab === ENTITY_TYPE_HREF_MAP.LOAN_AND_LOCK &&
    section === LOAN_AND_LOCK_SECTION_HREF_MAP.CUSTOM_FIELDS,
});

const filterPropertySectionHref = ({
  href,
  isPurchase,
  isAdditionalLoan,
  isHelocPurposePurchase,
  isUnsecured,
  isChattel,
  shouldShowTransactionDetailsSection,
  shouldShowPropertyCustomFieldsSection,
  isAppraisalSelectionViewAllowed,
}) => {
  if (isUnsecured) {
    return false;
  }

  // LEN-156: a chattel loan keys its property taxes (escrow lines) and homeowners insurance on the
  // standard subject property, so only the Property Expenses section is reachable for it.
  if (isChattel) {
    return href === PROPERTY_SECTION_HREF_MAP.PROPERTY_EXPENSES;
  }

  switch (href) {
    case PROPERTY_SECTION_HREF_MAP.AGENT_AND_SELLER:
      return isPurchase || (isAdditionalLoan && isHelocPurposePurchase);

    case PROPERTY_SECTION_HREF_MAP.TRANSACTION_DETAILS:
      return shouldShowTransactionDetailsSection;

    case PROPERTY_SECTION_HREF_MAP.CUSTOM_FIELDS:
      return shouldShowPropertyCustomFieldsSection;

    case PROPERTY_SECTION_HREF_MAP.APPRAISAL:
      return isAppraisalSelectionViewAllowed;

    default:
      return true;
  }
};

const filterLoanAndLockSectionHref = ({
  href,
  isPurchase,
  isAdditionalLoan,
  isHelocPurposePurchase,
  isUnsecured,
  isChattel,
  shouldShowLoanRolesSection,
  shouldShowLoanTagsSection,
  shouldShowProfitAndLossSection,
  shouldShowOBCustomFieldsSection,
  shouldShowApplicationCustomFieldsSection,
  shouldShowMortgageInsuranceSection,
}) => {
  switch (href) {
    case LOAN_AND_LOCK_SECTION_HREF_MAP.ADDITIONAL_FINANCING:
      return (
        !isUnsecured && !isChattel && (isPurchase || (isAdditionalLoan && isHelocPurposePurchase))
      );

    case LOAN_AND_LOCK_SECTION_HREF_MAP.LOAN_TAGS:
      return shouldShowLoanTagsSection;

    case LOAN_AND_LOCK_SECTION_HREF_MAP.LOAN_ROLES:
      return shouldShowLoanRolesSection;

    case LOAN_AND_LOCK_SECTION_HREF_MAP.LOAN_PL:
    case LOAN_AND_LOCK_SECTION_HREF_MAP.SELL_SIDE_LOCK:
      return shouldShowProfitAndLossSection;

    case LOAN_AND_LOCK_SECTION_HREF_MAP.OB_CUSTOM_FIELDS:
      return shouldShowOBCustomFieldsSection;

    case LOAN_AND_LOCK_SECTION_HREF_MAP.MORTGAGE_INSURANCE:
      return shouldShowMortgageInsuranceSection;

    case LOAN_AND_LOCK_SECTION_HREF_MAP.CUSTOM_FIELDS:
      return shouldShowApplicationCustomFieldsSection;

    default:
      return true;
  }
};

const getPropertySectionHrefsList = filterProps => {
  return Object.values(PROPERTY_SECTION_HREF_MAP).filter(tabHref =>
    filterPropertySectionHref({
      href: tabHref,
      ...filterProps,
    }),
  );
};

const getLoanAndLockSectionHrefsList = filterProps => {
  return Object.values(LOAN_AND_LOCK_SECTION_HREF_MAP).filter(tabHref =>
    filterLoanAndLockSectionHref({
      href: tabHref,
      ...filterProps,
    }),
  );
};

const getContactsSectionHrefsList = ({ contactsView }) => {
  return contactsView?.map(({ id }) => id) ?? [];
};

const getChattelSectionHrefsList = () => Object.values(CHATTEL_SECTION_HREF_MAP);

export const GET_ENTITY_SECTION_HREFS_LIST_FUNCTION_MAP = {
  [ENTITY_TYPE_HREF_MAP.PROPERTY_INFO]: getPropertySectionHrefsList,
  [ENTITY_TYPE_HREF_MAP.LOAN_AND_LOCK]: getLoanAndLockSectionHrefsList,
  [ENTITY_TYPE_HREF_MAP.CONTACTS]: getContactsSectionHrefsList,
  [ENTITY_TYPE_HREF_MAP.CHATTEL]: getChattelSectionHrefsList,
};

const ENTITY_DEFAULT_SECTION_MAP = {
  [ENTITY_TYPE_HREF_MAP.PROPERTY_INFO]: PROPERTY_SECTION_HREF_MAP.PROPERTY,
  [ENTITY_TYPE_HREF_MAP.LOAN_AND_LOCK]: LOAN_AND_LOCK_SECTION_HREF_MAP.LOAN_INFO,
  [ENTITY_TYPE_HREF_MAP.CHATTEL]: CHATTEL_SECTION_HREF_MAP.PROPERTY,
};

const ENTITY_DYNAMIC_SECTION_GETTER = {
  [ENTITY_TYPE_HREF_MAP.CONTACTS]: ({ contactsView, deletedRecords, deletedInitialRecords }) =>
    contactsView?.find(
      ({ id }) => !deletedRecords?.includes(id) && !deletedInitialRecords?.includes(id),
    )?.id,
};

export const getSectionToRedirectByEntityType = ({
  entityType,
  isChattel = false,
  ...dynamicSectionProps
}) => {
  if (ENTITIES_WITH_DYNAMIC_SECTIONS.includes(entityType)) {
    return ENTITY_DYNAMIC_SECTION_GETTER[entityType](dynamicSectionProps);
  }

  // LEN-156: Property Expenses is the only Property Info section a chattel loan has.
  if (isChattel && entityType === ENTITY_TYPE_HREF_MAP.PROPERTY_INFO) {
    return PROPERTY_SECTION_HREF_MAP.PROPERTY_EXPENSES;
  }

  return ENTITY_DEFAULT_SECTION_MAP[entityType];
};

export const PROPERTY_SECTION_NAME_MAP = {
  [PROPERTY_SECTION_HREF_MAP.PROPERTY]: 'Property',
  [PROPERTY_SECTION_HREF_MAP.PROPERTY_EXPENSES]: 'Property Expenses',
  [PROPERTY_SECTION_HREF_MAP.AGENT_AND_SELLER]: 'Agent and Seller',
  [PROPERTY_SECTION_HREF_MAP.APPRAISAL]: 'Appraisal',
  [PROPERTY_SECTION_HREF_MAP.PROJECT]: 'Project',
  [PROPERTY_SECTION_HREF_MAP.TRANSACTION_DETAILS]: 'Transaction Details',
  [PROPERTY_SECTION_HREF_MAP.CUSTOM_FIELDS]: 'Custom Fields',
};

const CHATTEL_SECTION_NAME_MAP = {
  [CHATTEL_SECTION_HREF_MAP.PROPERTY]: 'Property',
  [CHATTEL_SECTION_HREF_MAP.PROPERTY_EXPENSES]: 'Property Expenses',
  [CHATTEL_SECTION_HREF_MAP.HOME_DETAILS]: 'Manufactured Home Details',
};

export const LOAN_AND_LOCK_SECTION_NAME_MAP = {
  [LOAN_AND_LOCK_SECTION_HREF_MAP.LOAN_INFO]: 'Loan Info',
  [LOAN_AND_LOCK_SECTION_HREF_MAP.LOCK_INFO]: 'Lock Info',
  [LOAN_AND_LOCK_SECTION_HREF_MAP.SELL_SIDE_LOCK]: 'Sell Side Lock',
  [LOAN_AND_LOCK_SECTION_HREF_MAP.LOAN_PL]: 'Loan Profit & Loss',
  [LOAN_AND_LOCK_SECTION_HREF_MAP.ADDITIONAL_FINANCING]: 'Additional Financing',
  [LOAN_AND_LOCK_SECTION_HREF_MAP.PROGRAMS_PRICING]: 'Programs & Pricing',
  [LOAN_AND_LOCK_SECTION_HREF_MAP.CREDITS]: 'Credits',
  [LOAN_AND_LOCK_SECTION_HREF_MAP.LOAN_TAGS]: 'Loan Tags',
  [LOAN_AND_LOCK_SECTION_HREF_MAP.LOAN_ROLES]: 'Loan Roles',
  [LOAN_AND_LOCK_SECTION_HREF_MAP.OB_CUSTOM_FIELDS]: 'OB Custom Fields',
  [LOAN_AND_LOCK_SECTION_HREF_MAP.MORTGAGE_INSURANCE]: 'Mortgage Insurance',
  [LOAN_AND_LOCK_SECTION_HREF_MAP.CUSTOM_FIELDS]: 'Custom Fields',
};

const getContactsNavigationTabsProps = ({ contactsView }) =>
  contactsView?.reduce(
    (
      acc,
      {
        id,
        [ContactsField.TYPE]: type,
        [ContactsField.NAME]: name,
        [ContactsField.COMPANY]: company,
      },
    ) => {
      const contactType = type || ValueStub.COMMON;
      const fullName =
        [name?.firstName, name?.lastName].filter(Boolean).join(' ') || company || ValueStub.COMMON;

      acc[id] = {
        displayName: `${contactType}: ${fullName}`,
        label: (
          <>
            {contactType}:{' '}
            <Typography data-truncated className="pointer-events-none" variant="regular12">
              {fullName}
            </Typography>
          </>
        ),
      };

      return acc;
    },
    {},
  ) ?? {};

const PROPERTY_SECTION_FIELDS_MAP = {
  [PROPERTY_SECTION_HREF_MAP.PROPERTY]: propertySectionFieldsKeys,
  [PROPERTY_SECTION_HREF_MAP.PROPERTY_EXPENSES]: propertyExpensesPendingChangesFieldsKeys,
  [PROPERTY_SECTION_HREF_MAP.AGENT_AND_SELLER]: agentsAndSellersPendingChangesSectionKey,
  [PROPERTY_SECTION_HREF_MAP.APPRAISAL]: [
    'appraisal',
    'appraisalSurcharges',
    APPRAISAL_ORDER_CONTACTS_KEY,
  ],
  [PROPERTY_SECTION_HREF_MAP.PROJECT]: ['project'],
  [PROPERTY_SECTION_HREF_MAP.TRANSACTION_DETAILS]: transactionDetailsFieldsKeys,
  [PROPERTY_SECTION_HREF_MAP.CUSTOM_FIELDS]: ['customFields'],
};

const CHATTEL_SECTION_FIELDS_MAP = {
  [CHATTEL_SECTION_HREF_MAP.PROPERTY]: chattelPropertySectionFieldsKeys,
  [CHATTEL_SECTION_HREF_MAP.HOME_DETAILS]: chattelHomeDetailsSectionFieldsKeys,
  [CHATTEL_SECTION_HREF_MAP.PROPERTY_EXPENSES]: chattelPropertyExpensesSectionFieldsKeys,
};

const LOAN_AND_LOCK_SECTION_FIELDS_MAP = {
  [LOAN_AND_LOCK_SECTION_HREF_MAP.LOAN_INFO]: loanInfoSectionFieldsKeys,
  [LOAN_AND_LOCK_SECTION_HREF_MAP.LOCK_INFO]: lockInfoSectionFieldsKeys,
  [LOAN_AND_LOCK_SECTION_HREF_MAP.SELL_SIDE_LOCK]: sellSideLockSectionFieldsKeys,
  [LOAN_AND_LOCK_SECTION_HREF_MAP.ADDITIONAL_FINANCING]: ['additionalMortgages'],
  [LOAN_AND_LOCK_SECTION_HREF_MAP.CREDITS]: ['credits'],
  [LOAN_AND_LOCK_SECTION_HREF_MAP.LOAN_TAGS]: ['loanTags'],
  [LOAN_AND_LOCK_SECTION_HREF_MAP.PROGRAMS_PRICING]: programAndPricingPendingChangesFields,
  [LOAN_AND_LOCK_SECTION_HREF_MAP.LOAN_ROLES]: ['loanRoles'],
  [LOAN_AND_LOCK_SECTION_HREF_MAP.LOAN_PL]: [
    'profitAndLoss.srpPaidOut',
    'profitAndLoss.hedgeCost',
    'profitAndLoss.netSellPrice',
    'profitAndLoss.srpFromInvestor',
  ],
  [LOAN_AND_LOCK_SECTION_HREF_MAP.OB_CUSTOM_FIELDS]: ['obCustomFields'],
  [LOAN_AND_LOCK_SECTION_HREF_MAP.MORTGAGE_INSURANCE]: mortgageInsuranceSectionFieldsKeys,
  [LOAN_AND_LOCK_SECTION_HREF_MAP.CUSTOM_FIELDS]: ['customFields'],
};

const filterPropertyInfoTabs = ({ tabs, filterProps }) => {
  return tabs.filter(([tabValue]) =>
    filterPropertySectionHref({
      href: tabValue,
      ...filterProps,
    }),
  );
};

const filterLoanAndLockTabs = ({ tabs, filterProps }) => {
  return tabs.filter(([tabValue]) =>
    filterLoanAndLockSectionHref({
      href: tabValue,
      ...filterProps,
    }),
  );
};

const ENTITY_SECTIONS_FILTER_FUNCTION_MAP = {
  [ENTITY_TYPE_HREF_MAP.PROPERTY_INFO]: filterPropertyInfoTabs,
  [ENTITY_TYPE_HREF_MAP.LOAN_AND_LOCK]: filterLoanAndLockTabs,
};

const ENTITY_SECTION_NAME_MAP = {
  [ENTITY_TYPE_HREF_MAP.PROPERTY_INFO]: PROPERTY_SECTION_NAME_MAP,
  [ENTITY_TYPE_HREF_MAP.LOAN_AND_LOCK]: LOAN_AND_LOCK_SECTION_NAME_MAP,
  [ENTITY_TYPE_HREF_MAP.CHATTEL]: CHATTEL_SECTION_NAME_MAP,
};

const ENTITY_DYNAMIC_SECTION_NAME_GETTER = {
  [ENTITY_TYPE_HREF_MAP.CONTACTS]: getContactsNavigationTabsProps,
};

export const getNavigationTabsPropsByEntityType = ({ entityType, ...dynamicSectionProps }) => {
  if (ENTITIES_WITH_DYNAMIC_SECTIONS.includes(entityType)) {
    return ENTITY_DYNAMIC_SECTION_NAME_GETTER[entityType]?.(dynamicSectionProps);
  }

  return ENTITY_SECTION_NAME_MAP[entityType];
};

const ENTITY_SECTION_FIELDS_MAP = {
  [ENTITY_TYPE_HREF_MAP.PROPERTY_INFO]: PROPERTY_SECTION_FIELDS_MAP,
  [ENTITY_TYPE_HREF_MAP.LOAN_AND_LOCK]: LOAN_AND_LOCK_SECTION_FIELDS_MAP,
  [ENTITY_TYPE_HREF_MAP.CHATTEL]: CHATTEL_SECTION_FIELDS_MAP,
};

const ENTITY_WITH_DYNAMIC_SECTIONS_FIELDS_MAP = {
  [ENTITY_TYPE_HREF_MAP.CONTACTS]: contactsFieldsKeys,
};

export const getEntityFieldsMap = ({ entityType, validationErrors }) => {
  const navigationTabsWithErrors = Object.keys(validationErrors);

  if (ENTITIES_WITH_DYNAMIC_SECTIONS.includes(entityType)) {
    return navigationTabsWithErrors.reduce((acc, id) => {
      acc[id] = ENTITY_WITH_DYNAMIC_SECTIONS_FIELDS_MAP[entityType];

      return acc;
    }, {});
  }

  return _.pickBy(ENTITY_SECTION_FIELDS_MAP[entityType], keys =>
    keys.some(key => !!_.get(validationErrors, key)),
  );
};

// The server recalculates or fills these (read-only in the form) when the user saves their own
// edit, and records them as pending changes too. They stay highlighted, but counting them would
// report one edit as several.
const ENTITY_SECTION_RECALCULATED_FIELDS_MAP = {
  [ENTITY_TYPE_HREF_MAP.LOAN_AND_LOCK]: {
    [LOAN_AND_LOCK_SECTION_HREF_MAP.LOAN_INFO]: [
      'totalLoanAmount',
      'hudFieldOfficeCode',
      'hudFieldOfficeName',
      'hudHocCode',
      'hudHocName',
      'fhaInspectorIdentifier',
    ],
    [LOAN_AND_LOCK_SECTION_HREF_MAP.PROGRAMS_PRICING]: ['principalAndInterest'],
  },
};

const ENTITY_SECTION_EXTRACT_PENDING_CHANGES_ACTIONS_MAP = _.mapValues(
  ENTITY_SECTION_FIELDS_MAP,
  (tab, entityType) =>
    _.mapValues(tab, (keys, section) => {
      const countedKeys = _.difference(
        keys,
        ENTITY_SECTION_RECALCULATED_FIELDS_MAP[entityType]?.[section],
      );

      return pendingChanges => {
        const { adjustments, ...fields } = _.pick(pendingChanges, [
          ...countedKeys,
          'pendingChangesActionType',
          'isSeparateChange',
        ]);

        return { ...adjustments, ...fields };
      };
    }),
);

const ENTITY_WITH_DYNAMIC_SECTIONS_EXTRACT_PENDING_CHANGES_ACTIONS_MAP = _.mapValues(
  ENTITY_WITH_DYNAMIC_SECTIONS_FIELDS_MAP,
  keys => pendingChanges => _.pick(pendingChanges, [...keys, 'pendingChangesActionType']),
);

const getEntityExtractPendingChangesActionsMap = (entityType, section) => {
  if (ENTITIES_WITH_DYNAMIC_SECTIONS.includes(entityType)) {
    return ENTITY_WITH_DYNAMIC_SECTIONS_EXTRACT_PENDING_CHANGES_ACTIONS_MAP[entityType];
  }

  return ENTITY_SECTION_EXTRACT_PENDING_CHANGES_ACTIONS_MAP[entityType]?.[section];
};

const getEntityNavigationTabsByEntityType = ({ entityType, newRecords, ...filterProps }) => {
  const tabsMap = getNavigationTabsPropsByEntityType({ entityType, ...filterProps });

  if (!tabsMap) {
    return [];
  }

  const tabs = Object.entries(tabsMap);
  const filteredTabs =
    ENTITY_SECTIONS_FILTER_FUNCTION_MAP[entityType]?.({ tabs, filterProps }) ?? tabs;

  return filteredTabs.reduceRight(
    (acc, [value, tabProps]) => {
      acc.unshift({
        label: tabProps?.label ?? tabProps,
        displayName: tabProps?.displayName ?? tabProps,
        value,
        extractPendingChangesAction: getEntityExtractPendingChangesActionsMap(entityType, value),
      });

      return acc;
    },
    [
      ...newRecords.map(id => ({
        label: '-',
        displayName: '-',
        value: id,
        extractPendingChangesAction: getEntityExtractPendingChangesActionsMap(entityType, id),
      })),
    ],
  );
};

const navTextButtonOwnerState = {
  disableHover: true,
};

export const getNavigationTabs = ({
  pendingChanges = {},
  applicationId,
  tab: pageTab,
  subtab,
  section,
  history,
  isPurchase,
  isAdditionalLoan,
  isHelocPurposePurchase,
  isUnsecured,
  isChattel,
  shouldShowLoanRolesSection,
  shouldShowLoanTagsSection,
  isNestedEntityEditActive,
  setIsNestedEntityEditActive,
  shouldShowProfitAndLossSection,
  shouldShowTransactionDetailsSection,
  shouldShowOBCustomFieldsSection,
  shouldShowMortgageInsuranceSection,
  shouldShowApplicationCustomFieldsSection,
  shouldShowPropertyCustomFieldsSection,
  isAppraisalSelectionViewAllowed,
  dispatch,
  invalidTabs,
  contactsView,
  newRecords,
  deletedRecords,
  deletedInitialRecords,
  onDeleteRecord,
  onRestoreRecord,
  isManageEntityAllowed,
  hiddenPageIds,
  hiddenFieldIds,
  navNeededByAreaId = {},
}) => {
  const { isPropertyCustomFieldsActive, isApplicationCustomFieldsActive } =
    getCustomFieldsSectionOverrides({ subtab, section });

  return getEntityNavigationTabsByEntityType({
    entityType: subtab,
    isPurchase,
    isAdditionalLoan,
    isHelocPurposePurchase,
    isUnsecured,
    isChattel,
    shouldShowLoanRolesSection,
    shouldShowLoanTagsSection,
    shouldShowProfitAndLossSection,
    shouldShowTransactionDetailsSection,
    shouldShowOBCustomFieldsSection,
    shouldShowMortgageInsuranceSection,
    shouldShowApplicationCustomFieldsSection:
      shouldShowApplicationCustomFieldsSection || isApplicationCustomFieldsActive,
    shouldShowPropertyCustomFieldsSection:
      shouldShowPropertyCustomFieldsSection || isPropertyCustomFieldsActive,
    isAppraisalSelectionViewAllowed,
    contactsView,
    newRecords,
  }).reduce((acc, tab) => {
    if (checkIsTabHidden(tab.value, hiddenPageIds, hiddenFieldIds)) {
      return acc;
    }

    const isEntityWithDynamicSections = ENTITIES_WITH_DYNAMIC_SECTIONS.includes(subtab);
    const sectionPendingChanges =
      tab.extractPendingChangesAction?.(
        isEntityWithDynamicSections ? pendingChanges[tab.value] : pendingChanges,
      ) ?? {};

    const { count: pendingChangesCount } = calculatePendingChanges({
      [tab.value]: sectionPendingChanges,
    });

    const isInitiallyDeleted = deletedInitialRecords.includes(tab.value);
    const withButton = isEntityWithDynamicSections && isManageEntityAllowed && !isInitiallyDeleted;
    const isDeleted = isInitiallyDeleted || deletedRecords.includes(tab.value);
    const onClick = isDeleted ? onRestoreRecord : onDeleteRecord;

    const { isNeeded: isSectionNeeded } = getNavNeededState(
      navNeededByAreaId,
      getTabAreaIdsBySectionHref(tab.value),
    );

    acc.push({
      ...tab,
      displayName: tab.displayName,
      label: (
        <Box data-is-deleted={isDeleted} sx={navTextWrapperStyles}>
          {!!pendingChangesCount && (
            <PendingChangeWrapper
              isSmall
              className="pending-changes-count-wrapper"
              pendingChangesActionType={PendingChangesActionType.MODIFIED}
              sx={pendingChangesCounterStyles}
              textSelector="span"
            >
              <Typography className="pending-changes-count" variant="regular12">
                {pendingChangesCount}
              </Typography>
            </PendingChangeWrapper>
          )}

          <Box sx={navTextContentStyles}>
            <PresenceSectionDot sectionId={tab.value} />
            <Typography
              data-truncated
              className="nav-tab-label truncate"
              sx={navTextStyles}
              variant="regular12"
            >
              {tab.label}
            </Typography>

            {invalidTabs.includes(tab.value) && <ErrorDotIndicator />}
            {isSectionNeeded && <NavNeededIndicator />}
          </Box>

          {withButton && (
            <IconButton
              disableHover
              className="nav-tab-button"
              color="primary"
              data-id={tab.value}
              data-testid={PropertyAndLoanTestIds.getNavTabDeleteButtonId(tab.value)}
              ownerState={navTextButtonOwnerState}
              sx={navTabButtonStyles}
              onClick={onClick}
            >
              {isDeleted ? <ResetIcon /> : <DeleteIcon />}
            </IconButton>
          )}
        </Box>
      ),
      isDeleted: isInitiallyDeleted,
      pendingChangesCount,
      customStyles: isSectionNeeded
        ? { ...navTabCustomStyles, ...navNeededRowStyles }
        : navTabCustomStyles,
      onClick: () => {
        const navigate = () =>
          history.push(
            LoansPaths.applicationManagement.generatePath({
              applicationId,
              entityId: tab.value,
              tab: pageTab,
              subtab,
            }),
          );

        if (isNestedEntityEditActive) {
          dispatch(
            openConfirmationDialogAction({
              type: ConfirmationModalType.WARNING,
              message: 'You are leaving this tab. Unsaved changes will be lost.',
              onAccept: () => {
                navigate();
                setIsNestedEntityEditActive(false);
              },
              onAcceptButtonName: 'Proceed',
              withButtons: true,
            }),
          );

          return;
        }

        navigate();
      },
    });

    return acc;
  }, []);
};

export const getHeadTabs = ({ pendingChangesCount, loanNumber }) => {
  return [
    {
      value: loanNumber,
      label: `Loan ${loanNumber || ''}`,
      pendingChangesCount,
    },
  ];
};

const PROPERTY_SECTION_CONTENT_MAP = {
  [PROPERTY_SECTION_HREF_MAP.PROPERTY]: {
    SectionContent: Property,
  },
  [PROPERTY_SECTION_HREF_MAP.AGENT_AND_SELLER]: {
    SectionContent: AgentAndSeller,
  },
  [PROPERTY_SECTION_HREF_MAP.APPRAISAL]: {
    SectionContent: Appraisal,
  },
  [PROPERTY_SECTION_HREF_MAP.PROPERTY_EXPENSES]: {
    SectionContent: PropertyExpenses,
  },
  [PROPERTY_SECTION_HREF_MAP.PROJECT]: {
    SectionContent: Project,
  },
  [PROPERTY_SECTION_HREF_MAP.TRANSACTION_DETAILS]: {
    SectionContent: TransactionDetails,
  },
  [PROPERTY_SECTION_HREF_MAP.CUSTOM_FIELDS]: {
    SectionContent: PropertyCustomFields,
  },
};

const LOAN_AND_LOCK_SECTION_CONTENT_MAP = {
  [LOAN_AND_LOCK_SECTION_HREF_MAP.LOAN_INFO]: {
    SectionContent: LoanInfo,
  },
  [LOAN_AND_LOCK_SECTION_HREF_MAP.LOCK_INFO]: {
    SectionContent: LockInfo,
  },
  [LOAN_AND_LOCK_SECTION_HREF_MAP.SELL_SIDE_LOCK]: {
    SectionContent: SellSideLock,
  },
  [LOAN_AND_LOCK_SECTION_HREF_MAP.ADDITIONAL_FINANCING]: {
    SectionContent: AdditionalFinancing,
  },
  [LOAN_AND_LOCK_SECTION_HREF_MAP.LOAN_PL]: {
    SectionContent: LoanPL,
  },
  [LOAN_AND_LOCK_SECTION_HREF_MAP.PROGRAMS_PRICING]: {
    SectionContent: ProgramsAndPricing,
  },
  [LOAN_AND_LOCK_SECTION_HREF_MAP.CREDITS]: {
    SectionContent: Credits,
  },
  [LOAN_AND_LOCK_SECTION_HREF_MAP.LOAN_TAGS]: {
    SectionContent: LoanTags,
  },
  [LOAN_AND_LOCK_SECTION_HREF_MAP.LOAN_ROLES]: {
    SectionContent: LoanRoles,
  },
  [LOAN_AND_LOCK_SECTION_HREF_MAP.OB_CUSTOM_FIELDS]: {
    SectionContent: OBCustomFields,
  },
  [LOAN_AND_LOCK_SECTION_HREF_MAP.MORTGAGE_INSURANCE]: {
    SectionContent: MortgageInsurance,
  },
  [LOAN_AND_LOCK_SECTION_HREF_MAP.CUSTOM_FIELDS]: {
    SectionContent: ApplicationCustomFields,
  },
};

const CHATTEL_SECTION_CONTENT_MAP = {
  [CHATTEL_SECTION_HREF_MAP.PROPERTY]: {
    SectionContent: ChattelProperty,
  },
  [CHATTEL_SECTION_HREF_MAP.HOME_DETAILS]: {
    SectionContent: ChattelHomeDetails,
  },
  [CHATTEL_SECTION_HREF_MAP.PROPERTY_EXPENSES]: {
    SectionContent: ChattelPropertyExpenses,
  },
};

export const ENTITY_SECTION_CONTENT_MAP = {
  [ENTITY_TYPE_HREF_MAP.PROPERTY_INFO]: PROPERTY_SECTION_CONTENT_MAP,
  [ENTITY_TYPE_HREF_MAP.LOAN_AND_LOCK]: LOAN_AND_LOCK_SECTION_CONTENT_MAP,
  [ENTITY_TYPE_HREF_MAP.CHATTEL]: CHATTEL_SECTION_CONTENT_MAP,
};

export const ENTITY_DYNAMIC_SECTION_CONTENT_MAP = {
  [ENTITY_TYPE_HREF_MAP.CONTACTS]: {
    SectionContent: Contacts,
  },
};

export const ENTITY_SECTION_FETCH_DATA_ACTION_MAP = {
  [ENTITY_TYPE_HREF_MAP.PROPERTY_INFO]: getPropertyAndLoanSummaryEditDataAction,
  [ENTITY_TYPE_HREF_MAP.LOAN_AND_LOCK]: getLoanAndLockSummaryEditDataAction,
  [ENTITY_TYPE_HREF_MAP.CONTACTS]: getLoanContactsAction,
  [ENTITY_TYPE_HREF_MAP.CHATTEL]: getChattelDetailsAction,
};

export const ENTITY_SECTION_SAVE_ACTION_MAP = {
  [ENTITY_TYPE_HREF_MAP.PROPERTY_INFO]: editPropertyAndLoanSummaryAction,
  [ENTITY_TYPE_HREF_MAP.LOAN_AND_LOCK]: editLoanAndLockSummaryAction,
  [ENTITY_TYPE_HREF_MAP.CONTACTS]: bulkUpdateLoanContactsAction,
  [ENTITY_TYPE_HREF_MAP.CHATTEL]: saveChattelDetailsAction,
};
